import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import { DialogContent } from '@mui/material/'
import Stack from '@mui/material/Stack'
import { ReactComponent as CloseIcon } from '../../../assets/images/Close.svg'
import { SvgIcon } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import { ValueNarrative } from '../queries/useGetValueNarrativesQuery'
import { useTransferValueNarrativeMutation } from '../queries/useTransferValueNarrativeMutation'
import { SelectInput } from '../../../shared/components/SelectInput'
import useValueNarrativeModal from './hooks/useValueNarrativeModal'
import { useValueNarratives } from '../useValueNarratives'

const TransferValueNarrativeModalContext = createContext<ReturnType<typeof useValueNarrativeModal> | undefined>(
    undefined
)

const TransferValueNarrativeModalProvider = ({ children }: PropsWithChildren<{}>) => {
    const transferValueNarrativeModal = useValueNarrativeModal()

    return (
        <TransferValueNarrativeModalContext.Provider value={transferValueNarrativeModal}>
            <TransferValueNarrativeModal />
            {children}
        </TransferValueNarrativeModalContext.Provider>
    )
}

export const useTransferValueNarrativeModalContext = () => {
    const context = useContext(TransferValueNarrativeModalContext)

    if (!context)
        throw new Error('useTransferValueNarrativeModalContext must be used within TransferValueNarrativeModalContext')

    const [valueNarrative, setValueNarrative] = useState<ValueNarrative | null>(null)

    useEffect(() => {
        if (context.valueNarrative) {
            setValueNarrative(context.valueNarrative)
        }
    }, [context.valueNarrative])

    return { ...context, valueNarrative }
}

const TransferValueNarrativeModal = () => {
    const { valueNarrative, isOpen, handleClose } = useTransferValueNarrativeModalContext()

    const {
        isCategoriesDataReady,
        dataWithoutPseudoCategories,
        selectedCategory,
        selectedNarrative,
        handleSelectNarrative,
        handleSelectCategory,
    } = useValueNarratives()

    const categories = dataWithoutPseudoCategories.sort((a, b) => a.order - b.order)

    const { mutateAsync, isLoading } = useTransferValueNarrativeMutation({
        onTransferValueNarrative: () => {
            handleClose()

            // Redirect to the narrative in the new category
            if (selectedNarrative?._id === valueNarrative?._id) {
                handleSelectNarrative(selectedCategoryId, valueNarrative._id, true)
                return
            }

            // Redirect to the category if no narrative is selected
            if (selectedCategory?._id && !selectedCategory?.isPseudoCategory) {
                handleSelectCategory(selectedCategoryId)
                return
            }
        },
    })

    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)

    useEffect(() => {
        setSelectedCategoryId(null)
    }, [isOpen])

    const handleSave = async () => {
        await mutateAsync({ ...valueNarrative, categoryId: selectedCategoryId })
    }

    const canSave = !!selectedCategoryId && !isLoading && isCategoriesDataReady

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            fullWidth
            aria-labelledby="form-dialog-title"
            maxWidth="sm"
            PaperProps={{
                sx: {
                    py: 4,
                    px: 8,
                    borderRadius: '24px',
                    boxShadow: '0px 15px 50px 0px rgba(0, 0, 0, 0.30)',
                },
            }}
        >
            <DialogTitle id="form-dialog-title" sx={{ p: 0 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    pb={1.5}
                    borderBottom="1px solid"
                    borderColor="figma.grayscale.stroke"
                >
                    <Typography variant="22x600" color="figma.grayscale.textHier1">
                        Transferring Value Narrative
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            p: 0.5,
                            color: 'figma.grayscale.textHier2',
                        }}
                    >
                        <SvgIcon component={CloseIcon} inheritViewBox fontSize="medium" color="inherit" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <Stack direction="column" gap={3} py={3}>
                    <Stack direction="row" gap={1}>
                        <Typography variant="16x500" color="figma.grayscale.textHier1">
                            Transferring from:
                        </Typography>
                        <Typography variant="16x700" color="figma.grayscale.textHier1">
                            {categories?.find((category) => category._id === valueNarrative?.categoryId)?.name}
                        </Typography>
                    </Stack>
                    <Stack direction="column" gap={1}>
                        <Typography variant="16x500" color="figma.grayscale.textHier1">
                            Transferring to:
                        </Typography>
                        <Stack width="50%">
                            <SelectInput
                                required
                                selected={selectedCategoryId}
                                onChange={setSelectedCategoryId}
                                options={categories
                                    ?.filter((category) => category._id !== valueNarrative?.categoryId)
                                    ?.map((category) => ({
                                        id: category._id,
                                        label: category.name,
                                    }))}
                                placeholder="No category selected"
                            />
                        </Stack>
                    </Stack>
                    <Stack direction="column" gap={1.5}>
                        <Typography variant="16x400" color="figma.grayscale.textHier1">
                            Selected Value Narrative:
                        </Typography>
                        <Typography variant="16x400" color="figma.grayscale.textHier2">
                            {valueNarrative?.name}
                        </Typography>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Box flex={1} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        onClick={handleClose}
                        // @ts-expect-error mui type
                        variant="outlined-sm-button"
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={!canSave}
                        onClick={handleSave}
                        // @ts-expect-error mui type
                        variant="contained-sm-button"
                        color="primary"
                        sx={{
                            '&.Mui-disabled': {
                                color: 'figma.primary2',
                            },
                        }}
                    >
                        Transfer
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}

export default TransferValueNarrativeModalProvider
